/**
 * A confetti cannon in the bottom-left corner, firing one sustained volley
 * that peaks around the top centre and flutter down across the window — the celebration for a patient device becoming linked to a practice. The Portal's
 * pairing dialog fires the same effect on the therapist's side of that moment
 * (portal `resources/js/utils/confetti.ts`); keep the two in step.
 *
 * Drawn on a canvas of our own rather than canvas-confetti's default one: the
 * dialogs this celebrates are modal `<dialog>`s in the browser's top layer,
 * which paints above any z-index, so a canvas in the ordinary page would fall
 * BEHIND the dialog and its blurred backdrop. Ours is a `popover`, and a
 * popover shown after the dialog opened stacks above it in the top layer.
 *
 * Popover needs iOS 17 / Chrome 114. Older engines (the app still targets
 * iOS 15) get the canvas inside the open dialog instead, which falls within the
 * dialog's box rather than across the screen — smaller, but still seen.
 */
import confetti from 'canvas-confetti'

/**
 * The bunting's palette: the CI green and the two blues of `GarlandBanner`.
 * The green is the Portal garland's — LingoTalk's own bunting is blues only.
 */
export const GARLAND_CONFETTI_COLORS = ['#44AC69', '#008ECF', '#0D4D80']

/**
 * One volley, held for a second rather than popped: a single burst reads as a
 * clump, several as a stutter, and a steady stream fills the arc evenly.
 */
const VOLLEY_MS = 1000
/** Pieces over the whole volley. */
const PIECES = 450
/** Degrees either side of the aim, in total — wide enough to fan out. */
const SPREAD = 55
/** Sizes, cycled one per shot; canvas-confetti fixes `scalar` per call. */
const SCALARS = [0.8, 1, 1.25]
/**
 * canvas-confetti, per tick at a fixed 60 a second: a piece moves `velocity`
 * along its launch angle and `3 × gravity` px down, and `velocity *= decay`.
 * With these two, a launched piece runs out of speed after about 41 ticks,
 * having covered ~13.6 × its launch speed and dropped ~123 × gravity px —
 * which is what `aim()` solves with. After the peak it falls at 3 × gravity px
 * a tick: 4.5, so a 900px window takes about three seconds.
 */
const DECAY = 0.93
const GRAVITY = 1.5
const REACH = 13.6
const PEAK_TICKS = 41

/**
 * Angle and launch speed that carry an average piece from the bottom-left
 * corner to the top centre (10% down) of this window. canvas-confetti then
 * scatters each piece's speed over 0.5–1.5× of it, so some fall short and some
 * overshoot the top edge and drop back in — which is what fills the screen.
 */
function aim(width: number, height: number): { angle: number; velocity: number } {
  const across = width / 2
  const up = height * 0.9 + PEAK_TICKS * 3 * GRAVITY
  return {
    angle: (Math.atan2(up, across) * 180) / Math.PI,
    velocity: Math.hypot(across, up) / REACH,
  }
}

export function celebrate(colors: string[] = GARLAND_CONFETTI_COLORS): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return Promise.resolve()
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return Promise.resolve()

  const canvas = document.createElement('canvas')
  // Nothing to draw on (jsdom, a locked-down browser): skip the effect. Asked
  // here because canvas-confetti only finds out inside its own animation
  // frame, where the failure escapes any try/catch of ours.
  if (canvas.getContext('2d') === null) return Promise.resolve()
  canvas.setAttribute('aria-hidden', 'true')
  // Inline, so it beats the UA's `[popover]` box (border, padding, margin,
  // Canvas background) as well as anything a stylesheet might add.
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    maxHeight: 'none',
    margin: '0',
    padding: '0',
    border: '0',
    background: 'transparent',
    overflow: 'visible',
    pointerEvents: 'none',
    zIndex: '2147483647',
  })

  const promoted = 'showPopover' in canvas
  const host = promoted ? document.body : (document.querySelector('dialog[open]') ?? document.body)
  host.appendChild(canvas)
  if (promoted) {
    canvas.setAttribute('popover', 'manual')
    try {
      canvas.showPopover()
    } catch {
      // Drawn in place, under the top layer — still harmless.
    }
  }

  const fire = confetti.create(canvas, { resize: true, useWorker: false })
  const remove = (): void => {
    fire.reset()
    canvas.remove()
  }

  const { angle, velocity } = aim(window.innerWidth, window.innerHeight)
  // Long enough to peak and fall out of the bottom; canvas-confetti fades a
  // piece out linearly over its lifetime, so the slack keeps it visible on
  // the way down instead of ghosting out halfway.
  const ticks = Math.ceil((PEAK_TICKS + window.innerHeight / (3 * GRAVITY)) * 1.6)

  return new Promise<void>((resolve) => {
    const finish = (): void => {
      remove()
      resolve()
    }
    const start = performance.now()
    let fired = 0
    let pending: Promise<null> | null = null

    const frame = (now: number): void => {
      // Paced by elapsed time, not frames, so a 120 Hz iPad fires the same
      // amount as a 60 Hz laptop.
      const due = Math.min(PIECES, Math.round(((now - start) / VOLLEY_MS) * PIECES))
      try {
        while (fired < due) {
          const count = Math.min(due - fired, 4)
          pending = fire({
            particleCount: count,
            origin: { x: 0, y: 1 },
            angle,
            spread: SPREAD,
            startVelocity: velocity,
            decay: DECAY,
            gravity: GRAVITY,
            drift: Math.random() * 0.6 - 0.2,
            scalar: SCALARS[fired % SCALARS.length],
            ticks,
            // One colour per shot, picked here: canvas-confetti assigns colours
            // by index within a call, so small calls would favour the first.
            colors: [colors[Math.floor(Math.random() * colors.length)] ?? '#0D4D80'],
          })
          fired += count
        }
      } catch {
        // Belt and braces: never let decoration break the success state.
        finish()
        return
      }

      if (fired < PIECES) requestAnimationFrame(frame)
      else void Promise.resolve(pending).finally(finish)
    }

    requestAnimationFrame(frame)
  })
}
